/*
 * Store suggestion pop-up and World / İstanbul header dropdown. Replaces the jQuery + Azure
 * geo-redirect (2026-10). Needs ModalDialog from global.js.
 *
 * - Country from Shopify: /browsing_context_suggestions.json (cached per tab session).
 * - world shows the pop-up to TR visitors, istanbul to everyone else.
 * - Shown after Shopify's cookie banner is answered.
 * - After any choice the pop-up stays hidden for 1 day (cookie store_popup).
 * - ?iloverumisu (10 days) and ?frommerchant (1 day) still hide it, as before.
 * - Links with ?store_switch=… hide it on the store the visitor lands on.
 * - ?test_country=TR (or any code) previews the pop-up for that country.
 * - Choices are sent to the custom pixel as `store_switch` events.
 */
(() => {
  const COOKIE = 'store_popup';
  const BOT = /bot|googlebot|crawler|spider|robot|crawling/i;

  const readCookie = name => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? match[1] : null;
  };

  const writeCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  };

  const publish = (data) => {
    try {
      window.Shopify?.analytics?.publish('store_switch', data);
    } catch (e) {}
  };

  const currentStore = () =>
    document.querySelector('[data-current-store]')?.dataset.currentStore || 'world';

  // Send the event, then follow the link (a short wait so the pixel gets the event).
  document.addEventListener('click', event => {
    const link = event.target.closest('a[data-store-switch]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    writeCookie(COOKIE, 'closed', 1);
    publish({
      banner_choice: link.closest('store-popup') ? 'popup_switch' : 'header',
      from_store: currentStore(),
      to_store: link.dataset.storeSwitch,
    });
    setTimeout(() => (window.location.href = link.href), 150);
  });

  // Close the header store dropdown on an outside click or Escape.
  const closeSwitchers = (except) =>
    document.querySelectorAll('details.store-switcher[open]').forEach(d => d !== except && d.removeAttribute('open'));
  document.addEventListener('click', event => closeSwitchers(event.target.closest('details.store-switcher')));
  document.addEventListener('keyup', event => event.key === 'Escape' && closeSwitchers());

  const getCountry = async () => {
    try {
      const cached = sessionStorage.getItem('store_popup_country');
      if (cached) return cached;
    } catch (e) {}
    const response = await fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
    const data = await response.json();
    const country = data?.detected_values?.country?.handle || '';
    try {
      sessionStorage.setItem('store_popup_country', country);
    } catch (e) {}
    return country;
  };

  // Hide the pop-up for visitors who arrive with a bypass or switch parameter.
  const handleParams = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('iloverumisu')) writeCookie(COOKIE, 'closed', 10);
    else if (url.searchParams.has('frommerchant') || url.searchParams.has('store_switch'))
      writeCookie(COOKIE, 'closed', 1);
    if (url.searchParams.has('store_switch')) {
      url.searchParams.delete('store_switch');
      history.replaceState(history.state, '', url.toString());
    }
  };

  // Wait for Shopify's cookie banner (it loads late and sits above every modal) and for
  // any other open pop-up, such as the season message.
  const whenBannerClosed = (callback) => {
    const busy = () => {
      const banner = document.getElementById('shopify-pc__banner');
      const bannerOpen = banner && getComputedStyle(banner).display !== 'none' && banner.getClientRects().length > 0;
      return bannerOpen || document.querySelector('.store-modal[open]');
    };
    setTimeout(function check() {
      if (busy()) setTimeout(check, 500);
      else callback();
    }, 1500);
  };

  class StorePopup extends ModalDialog {
    connectedCallback() {
      super.connectedCallback();
      if (this.started) return;
      this.started = true;
      if (window.Shopify?.designMode || BOT.test(navigator.userAgent)) return;
      // Review aid: ?test_country=TR shows the pop-up as for a visitor from that country.
      const testCountry = new URL(window.location.href).searchParams.get('test_country');
      handleParams();
      if (readCookie(COOKIE) && !testCountry) return;

      (testCountry ? Promise.resolve(testCountry.toUpperCase()) : getCountry())
        .then(country => {
          if (!country) return;
          const isTR = country === 'TR';
          const show = this.dataset.currentStore === 'world' ? isTR : !isTR;
          if (show) whenBannerClosed(() => this.show());
        })
        .catch(() => {});
    }

    hide() {
      super.hide();
      if (this.hasAttribute('data-choice-sent')) return;
      this.setAttribute('data-choice-sent', '');
      writeCookie(COOKIE, 'closed', 1);
      publish({
        banner_choice: 'popup_stay',
        from_store: this.dataset.currentStore,
        to_store: this.dataset.currentStore,
      });
    }
  }

  customElements.define('store-popup', StorePopup);
})();
